<template>
  <Teleport to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="visible"
      :size="'md'"
      test-id="add-customer-modal"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'"> {{ t("add-customer-button") }}</cl-heading>
      </template>
      <div>
        <section class="tw-mt-6 tw-mb-10">
          <cl-stepper :steps="steps" />
        </section>
        <customer-step-index
          v-if="customerStep.current"
          v-model:form="customerForm"
          @form-validation="isCustomerFormValid = $event"
          :is-current-step="customerStep.current"
        />
        <solutions-step-index
          v-if="solutionsStep.current"
          :form="solutionsForm"
          @update:form="Object.assign(solutionsForm, $event)"
          @form-validation="isSolutionsFormValid = $event"
          :is-current-step="solutionsStep.current"
          :is-country-available="isCountryAvailable"
          :all-licences="listAllLicences"
        />
      </div>
      <!-- footer -->
      <template #[`footer.decline`]>
        <cl-button
          data-test-id="create-customer-btn-cancel"
          :variant="'secondary-ghost'"
          @on-click="onClose"
        >
          <span>{{ t("cancel") }}</span>
        </cl-button>
        <template v-if="solutionsStep.current">
          <cl-button
            :variant="'secondary-outline'"
            data-test-id="create-customer-input-btn-go-back-to-solutions-step"
            @on-click="goToStep(1)"
          >
            <span>{{ t("back") }}</span>
          </cl-button>
        </template>
      </template>
      <template #[`footer.accept`]>
        <template v-if="customerStep.current">
          <cl-button
            variant="secondary"
            data-test-id="create-customer-btn-go-to-customers-step"
            :disabled="isCustomerFormValid"
            @on-click="
              () => {
                customerStep.completed = true;
                goToStep(2);
              }
            "
          >
            <span>{{ t("next") }}</span>
          </cl-button>
        </template>
        <template v-if="solutionsStep.current">
          <cl-button v-if="isRequestPending" :variant="'secondary'" disabled>
            <cl-spinner
              :loading-text="$t('loading')"
              :size="'small'"
              class="mr-2"
            />
            <span>{{ t("saving") }}</span>
          </cl-button>
          <cl-button
            v-else
            variant="secondary"
            data-test-id="save-customer-btn-save-save"
            :disabled="
              isSolutionsFormValid ||
              (!solutionsForm.emailSecurity.switcher &&
                !solutionsForm.phishingSimulation.switcher &&
                !solutionsForm.sat.switcher &&
                !solutionsForm.emailArchiving.switcher)
            "
            @on-click="saveCustomer"
          >
            <span>{{ t("add-customer-button") }}</span>
          </cl-button>
        </template>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script lang="ts">
export interface TAddCustomersProps {
  visible: boolean;
  mspObj: Organisation;
  listAllLicences: ResellerOrganisationResponse;
}
</script>
<script setup lang="ts">
import { Ref } from "vue";
import { computed, reactive, ref, toRefs } from "vue";

import type { Step } from "@cybersentriq/frontend-component-library/dist/types/src/components/stepper/types";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useArctitanStore } from "@/store/arctitan.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useI18n } from "vue-i18n";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
} from "@/utils/services";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import CustomerStepIndex from "./customer-step/customer-step.index.vue";
import SolutionsStepIndex from "./solutions-step/solutions-step.index.vue";

import { DATA_TABLE_COOKIE, TIER } from "@/constants";
import { Organisation } from "@/types/organisatio";
import { useCookiesDataTable } from "@/composables";
import { ResellerOrganisationResponse } from "@/types/licenceSolutions.ts";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import { useActiveSolutions } from "@/composables/useActiveSolutions";
import { useFlag } from "@/composables/useFlags/useFlag";
import CustomerCreatedToast from "./customer-created-toast/customer-created-toast.index.vue";

const toastStore = useToastStore();

const cookieKey = DATA_TABLE_COOKIE.SYSTEM_MSPS_LIST;
const { reqParams } = useCookiesDataTable(
  {
    sortByKey: `${cookieKey}-sort-by`,
    sortOrdKey: `${cookieKey}-sort-order`,
    rowsPerPageKey: `${cookieKey}-rows-per-page`,
    selectedColumnsKey: `${cookieKey}-selected-columns`,
  },
  {
    sortBy: "name",
    sortOrd: "asc",
  },
);
const props = defineProps<TAddCustomersProps>();
const { visible, mspObj, listAllLicences } = toRefs(props);
const { t } = useI18n();
const organisationsStore = useOrganisationsStore();
const licenceStore = useLicenceStore();
const arctitanStore = useArctitanStore();
const { mspGlobalOrganisationId } = useGlobalOrganisationID();
const isCustomerFormValid = ref<boolean>(false);
const isSolutionsFormValid = ref<boolean>(false);
const { setRequestStatus, isRequestPending } = useRequestStatus({
  type: READY,
});
const { isSolutionActive } = useActiveSolutions();
const { isFeatureAvailable: isCustomerCreatedToastV2 } = useFlag(
  "CUSTOMER_CREATED_TOAST_V2",
);

const customerForm = ref({
  name: "",
  description: "",
});

const solutionsForm = reactive({
  licensesByGroup: {
    seats_purchased: "",
  },
  emailSecurity: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    switcher: false,
  },
  emailArchiving: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    switcher: false,
  },
  phishingSimulation: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    switcher: false,
  },
  sat: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    switcher: false,
  },
});

const customerStep = ref<Step>({
  index: 1,
  label: t("step-add-customers"),
  current: true,
  completed: false,
  actionable: false,
});
const solutionsStep = ref<Step>({
  index: 2,
  label: t("step-add-solutions"),
  current: false,
  completed: false,
  actionable: false,
});
const isCountryAvailable = computed<boolean>(() => {
  return !!mspObj.value?.country;
});
const steps = computed<Step[]>(() => [customerStep.value, solutionsStep.value]);
const stepsRefs = [customerStep, solutionsStep];

const goToStep = (index: number | string) => {
  for (const step of stepsRefs) {
    if (index === step.value.index) {
      step.value.current = true;
      step.value.completed = false;

      continue;
    }

    step.value.current = false;
  }
};

const creatingCustomerResponse: Ref<Organisation> = ref({
  uuid: "",
  name: "",
  created_at: "",
  country: "",
  organisation_type: "",
  services: [],
});

const saveCustomer = async () => {
  try {
    setRequestStatus({ type: PENDING });
    const orgs = await organisationsStore.createOrginisation({
      formData: {
        ...customerForm.value,
        ...solutionsForm,
      },
      parent_uuid: mspGlobalOrganisationId.value,
      organisation_type: TIER.customer,
    });
    creatingCustomerResponse.value = orgs.data;

    if (orgs.status >= 200 && orgs.status < 300) {
      orgs.data.country !== undefined && isSolutionActive(ARC_SERVICE)
        ? await hasEmailArchivingTag()
        : await createLicenses();
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  } finally {
    // Ensure the modal never stays stuck in the loading state, even if
    // customer creation fails or the success branch is skipped.
    setRequestStatus({ type: READY });
  }
};

const hasEmailArchivingTag = async () => {
  try {
    await arctitanStore.hasTagCustomer({
      formData: creatingCustomerResponse.value,
      uuid: mspGlobalOrganisationId.value,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  } finally {
    await createLicenses();
  }
};
const createLicenses = async () => {
  if (solutionsForm.emailSecurity.switcher) {
    await createEmailSecurityLicenses();
  }
  if (solutionsForm.phishingSimulation.switcher) {
    await createPhishingSimLicenses();
  }
  if (solutionsForm.sat.switcher) {
    await createSATLicenses();
  }
  if (solutionsForm.emailArchiving.switcher) {
    await enableEmailArchiving();
  }
  await finished();
};
const createEmailSecurityLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISH_SERVICE,
      formData: solutionsForm.emailSecurity,
      uuid: creatingCustomerResponse.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createPhishingSimLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISHING_SIMULATION,
      formData: solutionsForm.phishingSimulation,
      uuid: creatingCustomerResponse.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createSATLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: SAT_SERVICE,
      formData: solutionsForm.sat,
      uuid: creatingCustomerResponse.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const enableEmailArchiving = async () => {
  //the user has clicker the slider
  try {
    await Promise.all([
      arctitanStore.createCustomerArctian({
        formData: creatingCustomerResponse.value,
        uuid: mspGlobalOrganisationId.value,
      }),
      licenceStore.createLicence({
        solution_uuid: ARC_SERVICE,
        uuid: creatingCustomerResponse.value.uuid,
        formData: solutionsForm.emailArchiving,
        organisation_type: TIER.customer,
      }),
    ]);
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const finished = async () => {
  setRequestStatus({ type: READY });
  emit("on-create");

  if (isCustomerCreatedToastV2.value) {
    toastStore.displayToast({
      title: t("success"),
      duration: 10000,
      variant: "success",
      testId: "add-customers-toast-success",
      slotComponent: CustomerCreatedToast,
      slotProps: { customerUuid: creatingCustomerResponse.value.uuid },
    });
  } else {
    toastStore.displayToast({
      title: t("success"),
      message: t("add-customer-message"),
      duration: 5000,
      variant: "success",
      testId: "add-customers-toast-success",
    });
  }

  try {
    await organisationsStore.createPhishTitanOrg(
      creatingCustomerResponse.value.uuid,
    );
    await organisationsStore.getOrginisationsList({
      parent_uuid: mspGlobalOrganisationId.value,
      params: reqParams,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-create"): void;
}>();
const onClose = () => {
  emit("on-close");
};
</script>
